import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Bell, Mail, MessageSquare, Hash, Shield, AlertTriangle, 
  Clock, CheckCircle, Send, Settings, Loader2 
} from 'lucide-react';

export default function NotificationSettings() {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [preferences, setPreferences] = useState({
    // Email
    email_enabled: true,
    email_sla_alerts: true,
    email_vulnerability_alerts: true,
    email_threat_alerts: true,
    email_compliance_alerts: true,
    email_daily_digest: false,
    // Slack
    slack_enabled: false,
    slack_webhook_url: '',
    slack_sla_alerts: true,
    slack_vulnerability_alerts: true,
    slack_threat_alerts: true,
    slack_compliance_alerts: false,
    // Teams
    teams_enabled: false,
    teams_webhook_url: '',
    teams_sla_alerts: true,
    teams_vulnerability_alerts: true,
    teams_threat_alerts: true,
    teams_compliance_alerts: false,
    // Settings
    min_severity_for_alerts: 'high',
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await api.get('/notifications/preferences');
      setPreferences(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await api.put('/notifications/preferences', preferences);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const testWebhook = async (type) => {
    setTesting(type);
    try {
      // First save the webhook URL
      await api.put('/notifications/preferences', {
        [`${type}_webhook_url`]: preferences[`${type}_webhook_url`],
        [`${type}_enabled`]: true
      });
      
      // Then test
      await api.post(`/notifications/test-webhook?webhook_type=${type}`);
      toast.success(`Test notification sent to ${type === 'slack' ? 'Slack' : 'Teams'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to test ${type} webhook`);
    } finally {
      setTesting(null);
    }
  };

  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`font-rajdhani font-semibold text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Notification Settings
          </h2>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Configure how you receive security alerts
          </p>
        </div>
        <Button onClick={savePreferences} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Save Preferences
        </Button>
      </div>

      <Tabs defaultValue="email" className="w-full">
        <TabsList className={`${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
          <TabsTrigger value="email" className="gap-2">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="slack" className="gap-2">
            <Hash className="w-4 h-4" />
            Slack
          </TabsTrigger>
          <TabsTrigger value="teams" className="gap-2">
            <MessageSquare className="w-4 h-4" />
            Teams
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="w-4 h-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Email Tab */}
        <TabsContent value="email" className="space-y-4 mt-4">
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <Mail className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Email Notifications</h3>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Receive alerts via email</p>
                </div>
              </div>
              <Switch
                checked={preferences.email_enabled}
                onCheckedChange={(checked) => updatePreference('email_enabled', checked)}
              />
            </div>

            {preferences.email_enabled && (
              <div className="space-y-3 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-400" />
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>SLA Alerts</span>
                  </div>
                  <Switch
                    checked={preferences.email_sla_alerts}
                    onCheckedChange={(checked) => updatePreference('email_sla_alerts', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-red-400" />
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Vulnerability Alerts</span>
                  </div>
                  <Switch
                    checked={preferences.email_vulnerability_alerts}
                    onCheckedChange={(checked) => updatePreference('email_vulnerability_alerts', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Threat Alerts</span>
                  </div>
                  <Switch
                    checked={preferences.email_threat_alerts}
                    onCheckedChange={(checked) => updatePreference('email_threat_alerts', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Compliance Alerts</span>
                  </div>
                  <Switch
                    checked={preferences.email_compliance_alerts}
                    onCheckedChange={(checked) => updatePreference('email_compliance_alerts', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-indigo-400" />
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Daily Digest</span>
                  </div>
                  <Switch
                    checked={preferences.email_daily_digest}
                    onCheckedChange={(checked) => updatePreference('email_daily_digest', checked)}
                  />
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Slack Tab */}
        <TabsContent value="slack" className="space-y-4 mt-4">
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                  <Hash className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Slack Notifications</h3>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Send alerts to Slack channel</p>
                </div>
              </div>
              <Switch
                checked={preferences.slack_enabled}
                onCheckedChange={(checked) => updatePreference('slack_enabled', checked)}
              />
            </div>

            {preferences.slack_enabled && (
              <div className="space-y-4 pt-4 border-t border-white/10">
                <div>
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Webhook URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="url"
                      placeholder="https://hooks.slack.com/services/..."
                      value={preferences.slack_webhook_url || ''}
                      onChange={(e) => updatePreference('slack_webhook_url', e.target.value)}
                      className={`flex-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : 'bg-white border-slate-200'}`}
                    />
                    <Button
                      onClick={() => testWebhook('slack')}
                      disabled={!preferences.slack_webhook_url || testing === 'slack'}
                      variant="outline"
                      size="sm"
                      className={isDarkMode ? 'border-white/10' : ''}
                    >
                      {testing === 'slack' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Create an incoming webhook in your Slack workspace settings
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>SLA Alerts</span>
                    <Switch
                      checked={preferences.slack_sla_alerts}
                      onCheckedChange={(checked) => updatePreference('slack_sla_alerts', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Vulnerability Alerts</span>
                    <Switch
                      checked={preferences.slack_vulnerability_alerts}
                      onCheckedChange={(checked) => updatePreference('slack_vulnerability_alerts', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Threat Alerts</span>
                    <Switch
                      checked={preferences.slack_threat_alerts}
                      onCheckedChange={(checked) => updatePreference('slack_threat_alerts', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Compliance Alerts</span>
                    <Switch
                      checked={preferences.slack_compliance_alerts}
                      onCheckedChange={(checked) => updatePreference('slack_compliance_alerts', checked)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams" className="space-y-4 mt-4">
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <MessageSquare className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Microsoft Teams Notifications</h3>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Send alerts to Teams channel</p>
                </div>
              </div>
              <Switch
                checked={preferences.teams_enabled}
                onCheckedChange={(checked) => updatePreference('teams_enabled', checked)}
              />
            </div>

            {preferences.teams_enabled && (
              <div className="space-y-4 pt-4 border-t border-white/10">
                <div>
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Webhook URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="url"
                      placeholder="https://outlook.office.com/webhook/..."
                      value={preferences.teams_webhook_url || ''}
                      onChange={(e) => updatePreference('teams_webhook_url', e.target.value)}
                      className={`flex-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : 'bg-white border-slate-200'}`}
                    />
                    <Button
                      onClick={() => testWebhook('teams')}
                      disabled={!preferences.teams_webhook_url || testing === 'teams'}
                      variant="outline"
                      size="sm"
                      className={isDarkMode ? 'border-white/10' : ''}
                    >
                      {testing === 'teams' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Create an incoming webhook connector in your Teams channel
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>SLA Alerts</span>
                    <Switch
                      checked={preferences.teams_sla_alerts}
                      onCheckedChange={(checked) => updatePreference('teams_sla_alerts', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Vulnerability Alerts</span>
                    <Switch
                      checked={preferences.teams_vulnerability_alerts}
                      onCheckedChange={(checked) => updatePreference('teams_vulnerability_alerts', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Threat Alerts</span>
                    <Switch
                      checked={preferences.teams_threat_alerts}
                      onCheckedChange={(checked) => updatePreference('teams_threat_alerts', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Compliance Alerts</span>
                    <Switch
                      checked={preferences.teams_compliance_alerts}
                      onCheckedChange={(checked) => updatePreference('teams_compliance_alerts', checked)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4 mt-4">
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Alert Settings</h3>
            
            <div className="space-y-4">
              <div>
                <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  Minimum Severity for Alerts
                </Label>
                <select
                  value={preferences.min_severity_for_alerts}
                  onChange={(e) => updatePreference('min_severity_for_alerts', e.target.value)}
                  className={`mt-1 w-full rounded-md p-2 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                >
                  <option value="critical">Critical Only</option>
                  <option value="high">High and Above</option>
                  <option value="medium">Medium and Above</option>
                  <option value="low">All Severities</option>
                </select>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Only receive vulnerability alerts for selected severity and above
                </p>
              </div>

              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Quiet Hours</Label>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      Suppress non-critical notifications during these hours
                    </p>
                  </div>
                  <Switch
                    checked={preferences.quiet_hours_enabled}
                    onCheckedChange={(checked) => updatePreference('quiet_hours_enabled', checked)}
                  />
                </div>

                {preferences.quiet_hours_enabled && (
                  <div className="flex gap-4 mt-3">
                    <div className="flex-1">
                      <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Start Time</Label>
                      <Input
                        type="time"
                        value={preferences.quiet_hours_start || '22:00'}
                        onChange={(e) => updatePreference('quiet_hours_start', e.target.value)}
                        className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : 'bg-white border-slate-200'}`}
                      />
                    </div>
                    <div className="flex-1">
                      <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>End Time</Label>
                      <Input
                        type="time"
                        value={preferences.quiet_hours_end || '08:00'}
                        onChange={(e) => updatePreference('quiet_hours_end', e.target.value)}
                        className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : 'bg-white border-slate-200'}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
